/**
 * SpatialAudioEngine
 *
 * Manages the Web Audio API AudioListener and provides
 * spatial audio utilities for the engine.
 *
 * @module @engine/audio/SpatialAudioEngine
 */

import type { AudioListenerState } from './types';

/**
 * Manages the AudioListener on the AudioContext for spatial/3D audio.
 */
export class SpatialAudioEngine {
  private _ctx: AudioContext;
  private _listenerState: AudioListenerState = {
    positionX: 0,
    positionY: 0,
    positionZ: 0,
    forwardX: 0,
    forwardY: 0,
    forwardZ: -1,
    upX: 0,
    upY: 1,
    upZ: 0,
  };

  // Velocity tracking for doppler
  private _prevPosX: number = 0;
  private _prevPosY: number = 0;
  private _prevPosZ: number = 0;
  private _velocityX: number = 0;
  private _velocityY: number = 0;
  private _velocityZ: number = 0;
  private _hasPrevPosition: boolean = false;

  // Doppler configuration (set from AudioListener component)
  private _dopplerFactor: number = 0;
  private _speedOfSound: number = 343;

  constructor(ctx: AudioContext) {
    this._ctx = ctx;
  }

  // ==========================================================================
  // DOPPLER
  // ==========================================================================

  get dopplerFactor(): number { return this._dopplerFactor; }
  set dopplerFactor(v: number) { this._dopplerFactor = Math.max(0, v); }

  get speedOfSound(): number { return this._speedOfSound; }
  set speedOfSound(v: number) { this._speedOfSound = Math.max(1, v); }

  /**
   * Update listener velocity from position delta.
   * Must be called once per frame AFTER the listener position has been set.
   */
  updateVelocity(deltaTime: number): void {
    if (this._hasPrevPosition && deltaTime > 0) {
      this._velocityX = (this._listenerState.positionX - this._prevPosX) / deltaTime;
      this._velocityY = (this._listenerState.positionY - this._prevPosY) / deltaTime;
      this._velocityZ = (this._listenerState.positionZ - this._prevPosZ) / deltaTime;
    }
    this._prevPosX = this._listenerState.positionX;
    this._prevPosY = this._listenerState.positionY;
    this._prevPosZ = this._listenerState.positionZ;
    this._hasPrevPosition = true;
  }

  /**
   * Compute the doppler pitch multiplier for a source based on relative velocity.
   *
   * Uses the classical doppler formula:
   *   f_observed = f_source × (speedOfSound + v_listener_radial) / (speedOfSound - v_source_radial)
   *
   * where radial velocities are the components along the listener↔source axis.
   *
   * @param srcX Source position X
   * @param srcY Source position Y
   * @param srcZ Source position Z
   * @param srcVelX Source velocity X (units/sec)
   * @param srcVelY Source velocity Y (units/sec)
   * @param srcVelZ Source velocity Z (units/sec)
   * @param srcDopplerFactor Per-source doppler factor multiplier
   * @returns Pitch multiplier (1.0 = no shift)
   */
  computeDopplerPitch(
    srcX: number, srcY: number, srcZ: number,
    srcVelX: number, srcVelY: number, srcVelZ: number,
    srcDopplerFactor: number = 1,
  ): number {
    const globalDF = this._dopplerFactor;
    if (globalDF === 0) return 1;

    const combinedDF = globalDF * srcDopplerFactor;
    if (combinedDF === 0) return 1;

    // Direction vector from source → listener
    const dx = this._listenerState.positionX - srcX;
    const dy = this._listenerState.positionY - srcY;
    const dz = this._listenerState.positionZ - srcZ;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Too close — skip doppler to avoid division artifacts
    if (dist < 0.001) return 1;

    const nx = dx / dist;
    const ny = dy / dist;
    const nz = dz / dist;

    // Radial velocity of listener towards source (positive = approaching)
    const vListenerRadial = this._velocityX * nx + this._velocityY * ny + this._velocityZ * nz;
    // Radial velocity of source towards listener (positive = approaching)
    const vSourceRadial = srcVelX * nx + srcVelY * ny + srcVelZ * nz;

    const s = this._speedOfSound;

    // Doppler formula: f_obs = f_src × (s + vL×df) / (s - vS×df)
    const numerator = s + vListenerRadial * combinedDF;
    const denominator = s - vSourceRadial * combinedDF;

    // Clamp: source moving at/past speed of sound
    if (denominator <= 0) return 2;

    const ratio = numerator / denominator;
    // Clamp to a reasonable audible range
    return Math.max(0.5, Math.min(2, ratio));
  }

  /** Current listener state */
  get listenerState(): Readonly<AudioListenerState> {
    return this._listenerState;
  }

  /**
   * Update the listener position (usually from the AudioListener component's Transform).
   */
  setListenerPosition(x: number, y: number, z: number = 0): void {
    this._listenerState.positionX = x;
    this._listenerState.positionY = y;
    this._listenerState.positionZ = z;
    this._applyListenerPosition();
  }

  /**
   * Update the listener orientation.
   *
   * @param fx Forward X
   * @param fy Forward Y
   * @param fz Forward Z
   * @param ux Up X
   * @param uy Up Y
   * @param uz Up Z
   */
  setListenerOrientation(
    fx: number, fy: number, fz: number,
    ux: number = 0, uy: number = 1, uz: number = 0,
  ): void {
    this._listenerState.forwardX = fx;
    this._listenerState.forwardY = fy;
    this._listenerState.forwardZ = fz;
    this._listenerState.upX = ux;
    this._listenerState.upY = uy;
    this._listenerState.upZ = uz;
    this._applyListenerOrientation();
  }

  /**
   * Set full listener state at once.
   */
  setListenerState(state: AudioListenerState): void {
    Object.assign(this._listenerState, state);
    this._applyListenerPosition();
    this._applyListenerOrientation();
  }

  /**
   * Update listener from a 2D transform (typical for 2D games).
   *
   * Coordinate mapping: game (x, y) → Web Audio (x, 0, -y).
   * At rotation=0 the listener faces "screen up" (+Y in game = -Z in WA),
   * which is the default Web Audio forward direction.  This ensures:
   *   • game +X offset → right ear
   *   • game +Y offset → forward (centred, no L/R bias)
   *
   * @param invertX  Flip left/right panning
   * @param invertY  Flip front/back depth
   * @param rotationOffset  Extra rotation (radians) added to transform rotation
   * @param followRotation  When false, ignore transform rotation entirely
   */
  updateFrom2DTransform(
    x: number, y: number, rotation: number,
    invertX: boolean = false,
    invertY: boolean = false,
    rotationOffset: number = 0,
    followRotation: boolean = true,
  ): void {
    const posX = invertX ? -x : x;
    const posZ = invertY ? y : -y;
    this.setListenerPosition(posX, 0, posZ);

    const effectiveRot = followRotation ? rotation + rotationOffset : rotationOffset;
    this.setListenerOrientation(
      -Math.sin(effectiveRot), 0, -Math.cos(effectiveRot),
      0, 1, 0,
    );
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private _applyListenerPosition(): void {
    const listener = this._ctx.listener;
    listener.positionX.setValueAtTime(this._listenerState.positionX, this._ctx.currentTime);
    listener.positionY.setValueAtTime(this._listenerState.positionY, this._ctx.currentTime);
    listener.positionZ.setValueAtTime(this._listenerState.positionZ, this._ctx.currentTime);
  }

  private _applyListenerOrientation(): void {
    const listener = this._ctx.listener;
    listener.forwardX.setValueAtTime(this._listenerState.forwardX, this._ctx.currentTime);
    listener.forwardY.setValueAtTime(this._listenerState.forwardY, this._ctx.currentTime);
    listener.forwardZ.setValueAtTime(this._listenerState.forwardZ, this._ctx.currentTime);
    listener.upX.setValueAtTime(this._listenerState.upX, this._ctx.currentTime);
    listener.upY.setValueAtTime(this._listenerState.upY, this._ctx.currentTime);
    listener.upZ.setValueAtTime(this._listenerState.upZ, this._ctx.currentTime);
  }
}
